import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Plus, Save, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { CommanServices } from '@/services/commanServices';

// Inline Type Creation Popover
export const InlineTypeCreator = ({onTypeCreated, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    severity: 'Medium'
  });

  const resetForm = () => {
    setFormData({ name: '', severity: 'Medium' });
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Check for duplicates first
      const checkResponse = await CommanServices.checkduplicateincidentclassifications(formData.name.trim());
      if (checkResponse.status) {
        const checkResult = await checkResponse.data;
        if (checkResult?.is_duplicate) {
          setError('This name already exists. Pick it from the list or use another name.');
          setIsLoading(false);
          return;
        }
  
        // Create new incident type
        const payload = {
          incident_type: formData.name.trim(),
          alert_level: formData.severity,
          incident_severity: formData.severity === 'High' ? 'HIGH' : 
                            formData.severity === 'Medium' ? 'MEDIUM' : 'LOW',
          event_triggers: [],
          description: `Created inline while reporting incident`,
          associated_locations: [],
          departments_units: [],
          need_admin_confirmation: false,
          auto_notify_crew: true,
          auto_notify_departments: true,
          created_inline: true // Flag to identify inline creation
        };
  
        const response = await CommanServices.addincidentclassifications(payload);
  
        if (response.status) {
          const newType = await response.data;
          onTypeCreated(newType);
          setOpen(false);
          resetForm();
        } else {
          setError(response.message || 'Failed to create incident type');
        }
        
      }

    } catch (error) {
      console.error('Error creating incident type:', error);
      setError('Network error occurred');
    }

    setIsLoading(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          disabled={disabled}
          className="text-blue-600 hover:text-blue-800 p-1 h-auto"
        >
          <Plus className="h-3 w-3 mr-1" />
          New
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 z-[1310]" side="bottom" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Create Incident Type</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setOpen(false); resetForm(); }}
              className="p-1 h-auto"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter incident type name"
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Severity
            </label>
            <Select 
              value={formData.severity} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))}
            >
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="High">
                  <Badge className="bg-red-100 text-red-800">High</Badge>
                </SelectItem>
                <SelectItem value="Medium">
                  <Badge className="bg-orange-100 text-orange-800">Medium</Badge>
                </SelectItem>
                <SelectItem value="Low">
                  <Badge className="bg-yellow-100 text-yellow-800">Low</Badge>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={handleSave}
            disabled={isLoading || !formData.name.trim()}
            className="w-full text-sm"
            size="sm"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              <>
                <Save className="h-3 w-3 mr-1" />
                Save
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Inline Event Creation Popover  
export const InlineEventCreator = ({ selectedTypeId, onEventCreated, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    severity: 'inherit' // Optional - will inherit from type if inherit
  });

  const resetForm = () => {
    setFormData({ name: '', severity: 'inherit' });
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Event name is required');
      return;
    }

    if (!selectedTypeId) {
      setError('Please select an incident type first');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get the existing incident classification to add the event to
      const getResponse = await CommanServices.getincidentclassificationsbyId(selectedTypeId);
      if ((getResponse.status)) {
        const existingClassification = await getResponse.data;
        
        // Check for duplicate event name within this type
        const existingEventNames = existingClassification.event_triggers?.map(et => et.event.toLowerCase()) || [];
        if (existingEventNames.includes(formData.name.trim().toLowerCase())) {
          setError('This event name already exists for this incident type. Pick it from the list or use another name.');
          setIsLoading(false);
          return;
        }
  
        // Create new event-trigger pair
        const newEventTrigger = {
          event: formData.name.trim(),
          triggers: [],
          severity: formData.severity && formData.severity !== 'inherit' ? formData.severity : null, // Event-level severity (optional)
          triggerSeverityOverrides: {}
        };
  
        // Add to existing event_triggers array
        const updatedEventTriggers = [...(existingClassification.event_triggers || []), newEventTrigger];
  
        // Update the incident classification
        const updatePayload = {
          ...existingClassification,
          event_triggers: updatedEventTriggers,
          updated_at: new Date().toISOString()
        };
  
        const response = await CommanServices.updateincidentclassifications(selectedTypeId, updatePayload);
  
        if (response.status) {
          const updatedClassification = await response.data;
          onEventCreated(newEventTrigger, updatedClassification);
          setOpen(false);
          resetForm();
        } else {
          setError(response.message || 'Failed to create event');
        }
      }
      
    } catch (error) {
      console.error('Error creating event:', error);
      setError('Network error occurred');
    }

    setIsLoading(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          disabled={disabled || !selectedTypeId}
          className="text-blue-600 hover:text-blue-800 p-1 h-auto"
        >
          <Plus className="h-3 w-3 mr-1" />
          New
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 z-[1310]" side="bottom" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Create Event</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setOpen(false); resetForm(); }}
              className="p-1 h-auto"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Event Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter event name"
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Default Severity <span className="text-gray-500">(optional)</span>
            </label>
            <Select 
              value={formData.severity} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Inherit from incident type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Inherit from incident type</SelectItem>
                <SelectItem value="High">
                  <Badge className="bg-red-100 text-red-800">High</Badge>
                </SelectItem>
                <SelectItem value="Medium">
                  <Badge className="bg-orange-100 text-orange-800">Medium</Badge>
                </SelectItem>
                <SelectItem value="Low">
                  <Badge className="bg-yellow-100 text-yellow-800">Low</Badge>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={handleSave}
            disabled={isLoading || !formData.name.trim() || !selectedTypeId}
            className="w-full text-sm"
            size="sm"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              <>
                <Save className="h-3 w-3 mr-1" />
                Save
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Inline Trigger Creation Popover
export const InlineTriggerCreator = ({ selectedTypeId, selectedEventName, onTriggerCreated, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    severityOverride: 'none' // High / Medium / Low / none
  });

  const resetForm = () => {
    setFormData({ name: '', severityOverride: 'none' });
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Trigger name is required');
      return;
    }

    if (!selectedTypeId || !selectedEventName) {
      setError('Please select an incident type and event first');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get the existing incident classification
      const getResponse = await CommanServices.getincidentclassificationsbyId(selectedTypeId);
      if (getResponse.status) {
        alert('Could not fetch incident classification');
        const existingClassification = await getResponse.data;
        
        // Find the event to add trigger to
        const eventIndex = existingClassification.event_triggers?.findIndex(et => et.event === selectedEventName);
        if (eventIndex === -1) {
          setError('Selected event not found');
          setIsLoading(false);
          return;
        }
  
        const targetEvent = existingClassification.event_triggers[eventIndex];
        
        // Check for duplicate trigger name within this event
        const existingTriggerNames = targetEvent.triggers?.map(t => t.toLowerCase()) || [];
        if (existingTriggerNames.includes(formData.name.trim().toLowerCase())) {
          setError('This trigger name already exists for this event. Pick it from the list or use another name.');
          setIsLoading(false);
          return;
        }
  
        // Add trigger to the event
        const updatedTriggers = [...(targetEvent.triggers || []), formData.name.trim()];
        
        // Update severity overrides if specified
        let updatedSeverityOverrides = { ...(targetEvent.triggerSeverityOverrides || {}) };
        if (formData.severityOverride && formData.severityOverride !== 'none') {
          updatedSeverityOverrides[formData.name.trim()] = formData.severityOverride;
        }
  
        // Update the event-trigger pair
        const updatedEventTriggers = [...existingClassification.event_triggers];
        updatedEventTriggers[eventIndex] = {
          ...targetEvent,
          triggers: updatedTriggers,
          triggerSeverityOverrides: updatedSeverityOverrides
        };
  
        // Update the incident classification
        const updatePayload = {
          ...existingClassification,
          event_triggers: updatedEventTriggers,
          updated_at: new Date().toISOString()
        };
  
       
        const response = await CommanServices.updateincidentclassifications(selectedTypeId, updatePayload);
  
        if (response.status) {
          const updatedClassification = await response.data;
          onTriggerCreated(formData.name.trim(), formData.severityOverride, updatedClassification);
          setOpen(false);
          resetForm();
        } else {
          setError(response.message || 'Failed to create trigger');
        }
      }
      
    } catch (error) {
      console.error('Error creating trigger:', error);
      setError('Network error occurred');
    }

    setIsLoading(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          disabled={disabled || !selectedTypeId || !selectedEventName}
          className="text-blue-600 hover:text-blue-800 p-1 h-auto"
        >
          <Plus className="h-3 w-3 mr-1" />
          New
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 z-[1310]" side="bottom" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Create Trigger</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setOpen(false); resetForm(); }}
              className="p-1 h-auto"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Trigger Name / Value <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter trigger name"
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Severity Override
            </label>
            <Select 
              value={formData.severityOverride} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, severityOverride: value }))}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="None (inherit from event/type)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (inherit from event/type)</SelectItem>
                <SelectItem value="High">
                  <Badge className="bg-red-100 text-red-800">High</Badge>
                </SelectItem>
                <SelectItem value="Medium">
                  <Badge className="bg-orange-100 text-orange-800">Medium</Badge>
                </SelectItem>
                <SelectItem value="Low">
                  <Badge className="bg-yellow-100 text-yellow-800">Low</Badge>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={handleSave}
            disabled={isLoading || !formData.name.trim() || !selectedTypeId || !selectedEventName}
            className="w-full text-sm"
            size="sm"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              <>
                <Save className="h-3 w-3 mr-1" />
                Save
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};